import React, { useState, useEffect, useMemo, memo } from 'react'
import ReactDOM from 'react-dom'
import { useParams } from 'react-router-dom'
import { useStateValue } from 'context/store'
import { getFromApiWithoutToken } from 'actions'
import { handleErrorMessage } from 'utils/customHooks/useApiRequest'
import { getApplicationId, getApplicationBaseUrl, createScriptElement, createLinkElement } from './utils'

const ROOT_ELEMENT_ID = 'MicrofrontendContainer'

function MicrofrontendLoader() {
   const { appName, operation } = useParams()

   const [mfSettings, setMfSettings] = useState(null)

   const [{ sessionUser = {} }, dispatch] = useStateValue()
   const { settings: { menus = [], resources = {} } = {} } = sessionUser

   const currentApplicationId = useMemo(() => getApplicationId(appName, menus), [appName, menus])

   useEffect(() => {
      // No recargar los scripts (Js, CSS) cuando es la misma appName y solo cambia la operation
      if (operation && mfSettings) {
         renderMicrofrontend(mfSettings)
      }
   }, [operation]) // eslint-disable-line react-hooks/exhaustive-deps

   useEffect(() => {
      // NOTA: el spinner se cierra en los catch si hay error
      // o en la carga del script del MF, es decir, dentro de createScriptElement()
      dispatch({ type: 'OPEN_SPINNER' })
      dispatch({ type: 'CLOSE_SNACKBAR' })
      const applicationBaseUrl = getApplicationBaseUrl(currentApplicationId, resources)
      fetchMicrofrontendInfo(applicationBaseUrl)
   }, [appName])

   useEffect(() => {
      if (mfSettings) {
         createMicrofrontendScriptElements(mfSettings)
         renderMicrofrontend(mfSettings)
      }
   }, [mfSettings])

   async function isThereAnyFileInGCP(fileUrl) {
      try {
         // NOTA: No aplica useApiRequest por el return de la data y manejo de spinner
         const file = await getFromApiWithoutToken(fileUrl)
         return file
      } catch (error) {
         dispatch({ type: 'CLOSE_SPINNER' })
         dispatch({
            type: 'OPEN_SNACKBAR',
            openMessage: {
               message: `${handleErrorMessage(error)} (${fileUrl})`,
               severity: 'error'
            }
         })
      }
   }

   async function fetchMicrofrontendInfo(applicationBaseUrl) {
      try {
         // NOTA: No aplica useApiRequest por issue en la Promise (revisar) y por manejo de spinner
         const hash = Date.now()
         const resourseUrl = `${applicationBaseUrl}/microfrontend-info.json?v=${hash}`
         const fetchedMfSettings = await getFromApiWithoutToken(resourseUrl)
         const jsFileUrl = `${applicationBaseUrl}${fetchedMfSettings.jsUrl}`
         // Este segundo request es solo para comprobar que el JS file existe en GCP
         const jsFile = await isThereAnyFileInGCP(jsFileUrl)
         if (jsFile) {
            setMfSettings(fetchedMfSettings)
         }
      } catch (error) {
         dispatch({ type: 'CLOSE_SPINNER' })
         dispatch({
            type: 'OPEN_SNACKBAR',
            openMessage: {
               message: `${handleErrorMessage(error)} (Microfrontend: Consultar Configuraciones y fuentes)`,
               severity: 'error'
            }
         })
      }
   }

   async function createMicrofrontendScriptElements({ jsUrl, cssUrl, dependencies }) {
      try {
         const applicationBaseUrl = getApplicationBaseUrl(currentApplicationId, resources)
         const mfScriptUrl = `${applicationBaseUrl}${jsUrl}`
         const mfScript = createScriptElement('mf-script', mfScriptUrl, dispatch)
         const mfStyleUrl = `${applicationBaseUrl}${cssUrl}`
         const mfStyle = createLinkElement('mf-style-link', mfStyleUrl)
         if (mfScript && mfStyle) {
            document.body.appendChild(mfScript)
            document.head.appendChild(mfStyle)
         }
         if (dependencies !== undefined) {
            for (const dep of dependencies) {
               dispatch({ type: 'OPEN_SPINNER' })
               if (dep.type === 'JS') {
                  const depScript = createScriptElement('mf-dep-script', `${dep?.librarySource}`, dispatch)
                  if (depScript) {
                     document.body.appendChild(depScript)
                  }
               } else if (dep.type === 'CSS') {
                  const depStyle = createLinkElement('mf-style-dep-link', `${dep?.librarySource}`)
                  if (depStyle) {
                     document.head.appendChild(depStyle)
                  }
               }
            }
         }
      } catch (error) {
         dispatch({ type: 'CLOSE_SPINNER' })
         dispatch({
            type: 'OPEN_SNACKBAR',
            openMessage: {
               message: `Se presentó un error cargando los recursos de la Aplicación, contacte a soporte (Microfrontend: Cargar recursos).`,
               severity: 'error'
            }
         })
      }
   }

   async function renderMicrofrontend({ htmlTag }) {
      try {
         const componentCreated = await React.createElement(htmlTag, { tab: operation, id: htmlTag }, null)
         ReactDOM.render(componentCreated, document.getElementById(ROOT_ELEMENT_ID))
      } catch (error) {
         dispatch({ type: 'CLOSE_SPINNER' })
         dispatch({
            type: 'OPEN_SNACKBAR',
            openMessage: {
               message: `Se presentó un error cargando la Aplicación, contacte a soporte (Microfrontend: Render).`,
               severity: 'error'
            }
         })
      }
   }

   return <div id={ROOT_ELEMENT_ID}></div>
}

export default memo(MicrofrontendLoader)
